import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  GraduationCap,
  Upload,
  Plus,
  Search,
  Archive,
  RotateCcw,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  CheckCircle,
  Loader2,
  GitBranch,
  Edit2,
  Eye,
  Building2,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission, UserRole } from '@/types/global.types';
import {
  fetchFilieresGrouped,
  archiveFiliere,
  restoreFiliere,
  clearFilieresCache,
  getFiliereById,
  getCampusInfo,
  getAllFiliereCampusLinks,
} from '@/services/filieresService';
import type { FiliereWithOptions, Filiere, FiliereWithHierarchy, CampusBasic, WriteGuard } from '@/types/filiere.types';
import CreateFiliereModal from '../components/CreateFiliereModal';
import UploadFilieresModal from '../components/UploadFilieresModal';
import ArchiveConfirmModal from '../components/ArchiveConfirmModal';
import ViewFiliereModal from '../components/ViewFiliereModal';
import { useCampusScopeWarning } from '../components/filieres/useCampusScopeWarning';

/** Detail page lives under `<role base>/academique/filieres/:id` for every role;
 *  strip whichever list alias we are on (gestion-pedagogique, academique, gestion-filieres). */
const LIST_ALIAS_SUFFIX = /\/(gestion-pedagogique|academique|gestion-filieres)(\/.*)?$/;

const GestionFilieresPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const { hasPermission } = usePermissions();
  const tenantId = user?.tenantId;
  const canManage = hasPermission(Permission.FILIERE_MANAGE);
  // A Chef de filière only sees the filières assigned to them (backend-scoped)
  const isChefDeFiliere = user?.role === UserRole.PROGRAM_HEAD;

  // State
  const [filieres, setFilieres] = useState<FiliereWithOptions[]>([]);
  const [allFilieres, setAllFilieres] = useState<FiliereWithOptions[]>([]); // For total count (includes archived)
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [expandedFilieres, setExpandedFilieres] = useState<Set<string>>(new Set());

  // View details modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewDetailsData, setViewDetailsData] = useState<FiliereWithHierarchy | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [viewIsOption, setViewIsOption] = useState(false);
  const [viewParentName, setViewParentName] = useState<string | undefined>(undefined);
  const [currentViewId, setCurrentViewId] = useState<string | null>(null);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<Filiere | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<Filiere | null>(null);

  // Campus filter state (only shown when multiple campuses exist)
  const [hasMultipleCampuses, setHasMultipleCampuses] = useState(false);
  const [campuses, setCampuses] = useState<CampusBasic[]>([]);
  const [selectedCampusFilter, setSelectedCampusFilter] = useState<string>('all');
  const [filiereCampusMap, setFiliereCampusMap] = useState<Map<string, { isCommon: boolean; campusIds: string[] }>>(new Map());

  // Campus scope: warn (never block) before acting on a filière of another campus
  const { guard: guardCampusScope, modal: campusWarningModal } = useCampusScopeWarning();
  const guardFiliereWrite = useCallback(
    (filiereId: string, action: () => void): void =>
      guardCampusScope(filiereCampusMap.get(filiereId)?.campusIds, action),
    [guardCampusScope, filiereCampusMap],
  );
  // Guard bound to the filière currently open in the details modal
  const viewGuardWrite: WriteGuard = useCallback(
    (action) => {
      if (currentViewId) guardFiliereWrite(currentViewId, action);
      else action();
    },
    [guardFiliereWrite, currentViewId],
  );

  // Wrapper functions that preserve scroll position
  const setSuccessPreserveScroll = useCallback((msg: string) => {
    const scrollY = window.scrollY;
    setSuccess(msg);
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });
  }, []);

  const setErrorPreserveScroll = useCallback((msg: string) => {
    const scrollY = window.scrollY;
    setError(msg);
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });
  }, []);

  // Toggle expanded state for a filière with options
  const toggleExpanded = (filiereId: string) => {
    setExpandedFilieres(prev => {
      const newSet = new Set(prev);
      if (newSet.has(filiereId)) {
        newSet.delete(filiereId);
      } else {
        newSet.add(filiereId);
      }
      return newSet;
    });
  };

  // Load filières
  const loadFilieres = useCallback(async (showLoadingState = true) => {
    if (!tenantId) return;

    if (showLoadingState) {
      setIsLoading(true);
    }
    setError(null);

    try {
      // Fetch displayed filières (filtered by showArchived)
      const data = await fetchFilieresGrouped(!showArchived);
      setFilieres(data);

      // Also fetch ALL filières (including archived) for accurate total count
      const allData = await fetchFilieresGrouped(false); // activeOnly = false
      setAllFilieres(allData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      if (showLoadingState) {
        setIsLoading(false);
      }
    }
  }, [tenantId, showArchived]);

  useEffect(() => {
    loadFilieres();
  }, [loadFilieres]);

  // Load campus info + every filière's campus links (one call, refreshed when the list changes)
  useEffect(() => {
    const loadCampusInfo = async () => {
      try {
        const info = await getCampusInfo();
        setHasMultipleCampuses(info.hasMultipleCampuses);
        setCampuses(info.campuses);

        if (info.hasMultipleCampuses) {
          const links = await getAllFiliereCampusLinks();
          setFiliereCampusMap(
            new Map(links.map((l) => [l.filiereId, { isCommon: l.isCommon, campusIds: l.campusIds }])),
          );
        }
      } catch (err) {
        setErrorPreserveScroll(
          err instanceof Error ? err.message : 'Erreur lors du chargement des campus des filières',
        );
      }
    };
    loadCampusInfo();
  }, [filieres.length, setErrorPreserveScroll]);

  // Filter filières by search (including options) and campus
  const filteredFilieres = filieres.filter((f) => {
    const query = searchQuery.toLowerCase();
    const matchesBase =
      f.filiere.toLowerCase().includes(query) ||
      f.codeFiliere.toLowerCase().includes(query) ||
      f.diplome.toLowerCase().includes(query);

    // Also check if any option matches
    const matchesOption = f.options.some(opt =>
      opt.filiere.toLowerCase().includes(query) ||
      opt.codeFiliere.toLowerCase().includes(query) ||
      (opt.optionName && opt.optionName.toLowerCase().includes(query))
    );

    const matchesSearch = matchesBase || matchesOption;

    // Apply campus filter if multiple campuses exist and a specific campus is selected
    if (hasMultipleCampuses && selectedCampusFilter !== 'all') {
      const campusInfo = filiereCampusMap.get(f.id);
      if (!campusInfo) return matchesSearch; // No campus info, show it
      if (campusInfo.isCommon) return matchesSearch; // Common to all, always show
      // Check if the filière is assigned to the selected campus
      if (!campusInfo.campusIds.includes(selectedCampusFilter)) return false;
    }

    return matchesSearch;
  });

  // Count filières (base filières only, not options as separate entries)
  // Total: count all base filières including archived
  const totalCount = allFilieres.length;
  // Active: count only active base filières
  const activeCount = allFilieres.filter(f => f.isActive).length;
  // With options: count base filières that have options
  const filieresWithOptions = allFilieres.filter(f => f.options.length > 0).length;

  // Handle archive
  const handleArchive = async (filiere: Filiere) => {
    const scrollY = window.scrollY;
    try {
      await archiveFiliere(filiere.id);
      setSuccess(`La filière "${filiere.filiere}" a été archivée`);
      setArchiveTarget(null);
      clearFilieresCache();
      // Don't show loading state to prevent scroll jump
      await loadFilieres(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'archivage');
    }
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });
  };

  // Handle restore
  const handleRestore = async (filiere: Filiere) => {
    const scrollY = window.scrollY;
    try {
      await restoreFiliere(filiere.id);
      setSuccess(`La filière "${filiere.filiere}" a été restaurée`);
      setRestoreTarget(null);
      clearFilieresCache();
      // Don't show loading state to prevent scroll jump
      await loadFilieres(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la restauration');
    }
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });
  };

  // Handle row click - expand to show options (only if has options)
  const handleRowClick = (filiere: FiliereWithOptions) => {
    if (filiere.options.length > 0) {
      toggleExpanded(filiere.id);
    }
    // If no options, clicking the row does nothing
  };

  // Handle "Modifier" button click - navigate to the detail page.
  // Built from the role base path so the `gestion-pedagogique` alias also works.
  const handleEditClick = (filiere: Filiere) => {
    const basePath = location.pathname.replace(LIST_ALIAS_SUFFIX, '');
    navigate(`${basePath}/academique/filieres/${filiere.id}`);
  };

  // Handle "Voir" button click - open modal with details
  const handleViewClick = async (filiereId: string, isOption = false, parentName?: string) => {
    setViewModalOpen(true);
    setIsLoadingDetails(true);
    setViewDetailsData(null);
    setViewIsOption(isOption);
    setViewParentName(parentName);
    setCurrentViewId(filiereId);

    try {
      const data = await getFiliereById(filiereId);
      setViewDetailsData(data);
    } catch (err) {
      setErrorPreserveScroll(err instanceof Error ? err.message : 'Erreur lors du chargement des détails');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // Close view modal
  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setViewDetailsData(null);
    setCurrentViewId(null);
  };

  // Reload details after semester/module changes
  const handleDetailsUpdate = async () => {
    if (currentViewId) {
      try {
        const data = await getFiliereById(currentViewId);
        setViewDetailsData(data);
      } catch (err) {
        setErrorPreserveScroll(err instanceof Error ? err.message : 'Erreur lors du rechargement');
      }
    }
  };

  // Clear messages after delay
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <GraduationCap className="w-7 h-7 text-primary-600" />
            Gestion des Filières
          </h1>
          <p className="text-gray-500 mt-1">
            Gérez les filières accréditées, leurs semestres, modules et éléments
          </p>
        </div>

        {canManage && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">Upload E-DEP</span>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Ajouter Filière</span>
            </button>
          </div>
        )}
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-green-700">{success}</span>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, code ou diplôme..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Campus Filter - Only shown when multiple campuses exist */}
          {hasMultipleCampuses && (
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              <select
                value={selectedCampusFilter}
                onChange={(e) => setSelectedCampusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="all">Tous les campus</option>
                {campuses.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.name}
                    {campus.isMainCampus ? ' (Principal)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Show Archived Checkbox */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-600">Afficher les filières archivées</span>
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
          </div>
        ) : isChefDeFiliere && allFilieres.length === 0 ? (
          <div className="text-center py-12">
            <GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune filière ne vous est affectée</h3>
            <p className="text-gray-500">Contactez votre Directeur Pédagogique.</p>
          </div>
        ) : filteredFilieres.length === 0 ? (
          <div className="text-center py-12">
            <GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune filière trouvée</h3>
            <p className="text-gray-500 mb-4">
              {searchQuery
                ? 'Aucune filière ne correspond à votre recherche'
                : 'Commencez par uploader un fichier E-DEP ou créer une filière manuellement'}
            </p>
            {!searchQuery && canManage && (
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  <Upload className="w-4 h-4" />
                  Upload E-DEP
                </button>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter Filière
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Filière
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Diplôme
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Durée
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Accréditation
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredFilieres.map((filiere) => (
                  <React.Fragment key={filiere.id}>
                    {/* Parent Filière Row */}
                    <tr
                      className={`hover:bg-gray-50 transition-colors ${
                        !filiere.isActive ? 'bg-gray-50 opacity-60' : ''
                      } ${filiere.options.length > 0 ? 'cursor-pointer' : ''}`}
                      onClick={() => handleRowClick(filiere)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {/* Expand/Collapse indicator for filières with options */}
                          {filiere.options.length > 0 ? (
                            <div className="w-6 h-6 flex items-center justify-center">
                              {expandedFilieres.has(filiere.id) ? (
                                <ChevronDown className="w-4 h-4 text-gray-500" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-500" />
                              )}
                            </div>
                          ) : (
                            <div className="w-6" />
                          )}
                          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                            <GraduationCap className="w-5 h-5 text-primary-600" />
                          </div>
                          <div className="font-medium text-gray-900 flex items-center gap-2">
                            {filiere.filiere}
                            {filiere.options.length > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                <GitBranch className="w-3 h-3" />
                                {filiere.options.length} option{filiere.options.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm font-mono">
                          {filiere.codeFiliere}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600">{filiere.diplome}</td>
                      <td className="px-6 py-4 text-gray-600">{filiere.nombreAnnees} ans</td>
                      <td className="px-6 py-4 text-gray-600">{filiere.anneeAccreditation}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          {/* View button - only for filières WITHOUT options */}
                          {filiere.options.length === 0 && (
                            <button
                              onClick={() => handleViewClick(filiere.id)}
                              className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                              title="Voir les détails"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          {/* Edit button - only if user has MANAGE permission */}
                          {canManage && (
                            <button
                              onClick={() => guardFiliereWrite(filiere.id, () => handleEditClick(filiere as unknown as Filiere))}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Modifier"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          )}
                          {/* Archive/Restore button - only if user has MANAGE permission */}
                          {canManage && (
                            filiere.isActive ? (
                              <button
                                onClick={() => guardFiliereWrite(filiere.id, () => setArchiveTarget(filiere as unknown as Filiere))}
                                className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                title="Archiver"
                              >
                                <Archive className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => guardFiliereWrite(filiere.id, () => setRestoreTarget(filiere as unknown as Filiere))}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Restaurer"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Option Rows (nested under parent) */}
                    {expandedFilieres.has(filiere.id) && filiere.options.map((option) => (
                      <React.Fragment key={option.id}>
                        <tr
                          className={`hover:bg-purple-50 transition-colors bg-gray-50/50 ${
                            !option.isActive ? 'opacity-60' : ''
                          }`}
                        >
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-3 pl-12">
                              <div className="w-2 h-8 border-l-2 border-b-2 border-purple-300 rounded-bl" />
                              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                                <GitBranch className="w-4 h-4 text-purple-600" />
                              </div>
                              <div>
                                <div className="font-medium text-gray-800 text-sm">
                                  {option.optionName || option.filiere}
                                </div>
                                <div className="text-xs text-purple-600">Option de {filiere.filiere}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-3">
                            <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs font-mono">
                              {option.codeFiliere}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-gray-500 text-sm">{filiere.diplome}</td>
                          <td className="px-6 py-3 text-gray-500 text-sm">Dernière année</td>
                          <td className="px-6 py-3 text-gray-500 text-sm">{filiere.anneeAccreditation}</td>
                          <td className="px-6 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {/* View button for option */}
                              <button
                                onClick={() => handleViewClick(option.id, true, filiere.filiere)}
                                className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                title="Voir les détails"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">{totalCount}</div>
          <div className="text-sm text-gray-500">Total filières</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-2xl font-bold text-green-600">{activeCount}</div>
          <div className="text-sm text-gray-500">Actives</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-2xl font-bold text-purple-600">{filieresWithOptions}</div>
          <div className="text-sm text-gray-500">Avec options</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-2xl font-bold text-primary-600">
            {[...new Set(allFilieres.map((f) => f.diplome))].length}
          </div>
          <div className="text-sm text-gray-500">Diplômes</div>
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateFiliereModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            setSuccessPreserveScroll('Filière créée avec succès');
            loadFilieres();
          }}
        />
      )}

      {showUploadModal && (
        <UploadFilieresModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            loadFilieres();
          }}
        />
      )}

      {archiveTarget && (
        <ArchiveConfirmModal
          filiere={archiveTarget}
          action="archive"
          onConfirm={() => handleArchive(archiveTarget)}
          onCancel={() => setArchiveTarget(null)}
        />
      )}

      {restoreTarget && (
        <ArchiveConfirmModal
          filiere={restoreTarget}
          action="restore"
          onConfirm={() => handleRestore(restoreTarget)}
          onCancel={() => setRestoreTarget(null)}
        />
      )}

      {/* View Filiere Details Modal */}
      {viewModalOpen && (
        <ViewFiliereModal
          filiere={viewDetailsData}
          isLoading={isLoadingDetails}
          isOption={viewIsOption}
          parentFiliereName={viewParentName}
          canManage={canManage}
          guardWrite={viewGuardWrite}
          onClose={handleCloseViewModal}
          onUpdate={handleDetailsUpdate}
          onError={setErrorPreserveScroll}
          onSuccess={setSuccessPreserveScroll}
        />
      )}

      {campusWarningModal}
    </div>
  );
};

export default GestionFilieresPage;
